/**
 * Admin LLM Analytics - Platform-wide LLM usage tracking
 *
 * Displays comprehensive LLM usage metrics including:
 * - Total costs and token consumption
 * - Performance metrics (latency, success rate)
 * - Usage breakdown by users, bots, and models
 */

import React, { useEffect, useState } from 'react'
import {
  DollarSign,
  Zap,
  Clock,
  Activity,
  Users,
  Bot,
  Cpu,
  TrendingUp,
  RefreshCw,
  AlertCircle,
  Calendar
} from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/Card'
import { Badge } from '../../../components/ui/Badge'
import { Button } from '../../../components/ui/Button'
import { adminLlmService } from '../../../services/adminLlm'

/**
 * Metric card component for key statistics
 */
interface MetricCardProps {
  title: string
  value: string | number
  subtitle?: string
  icon: React.ComponentType<{ className?: string }>
  iconColor?: string
}

const MetricCard: React.FC<MetricCardProps> = ({
  title,
  value,
  subtitle,
  icon: Icon,
  iconColor = 'text-blue-600'
}) => {
  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-600">{title}</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
            {subtitle && (
              <p className="text-xs text-gray-500 mt-1">{subtitle}</p>
            )}
          </div>
          <div className={`p-3 bg-blue-50 rounded-full`}>
            <Icon className={`w-6 h-6 ${iconColor}`} />
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

/**
 * Format latency with appropriate time unit
 */
const formatLatency = (ms: number): string => {
  if (ms >= 60000) {
    // >= 1 minute, show in minutes
    const minutes = (ms / 60000).toFixed(2)
    return `${minutes}min`
  } else if (ms >= 1000) {
    // >= 1 second, show in seconds
    const seconds = (ms / 1000).toFixed(2)
    return `${seconds}s`
  } else {
    // < 1 second, show in milliseconds
    return `${Math.round(ms)}ms`
  }
}

export default function AdminLlmAnalytics() {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [overview, setOverview] = useState<any>(null)
  const [byUser, setByUser] = useState<any[]>([])
  const [byBot, setByBot] = useState<any[]>([])
  const [byModel, setByModel] = useState<any[]>([])
  const [trainingUsage, setTrainingUsage] = useState<any[]>([])
  const [refreshing, setRefreshing] = useState(false)

  // Date filter state
  const [dateRange, setDateRange] = useState<'today' | 'yesterday' | '7d' | '30d' | '90d' | 'all' | 'custom'>('today')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')

  const getDateParams = () => {
    const now = new Date()
    let start: string | undefined
    let end: string | undefined

    if (dateRange === 'custom') {
      if (startDate) start = startDate
      if (endDate) end = endDate
    } else if (dateRange === 'today') {
      start = now.toISOString().split('T')[0]
      end = now.toISOString().split('T')[0]
    } else if (dateRange === 'yesterday') {
      const yesterday = new Date(now)
      yesterday.setDate(now.getDate() - 1)
      start = yesterday.toISOString().split('T')[0]
      end = yesterday.toISOString().split('T')[0]
    } else if (dateRange !== 'all') {
      const days = parseInt(dateRange)
      const startDateTime = new Date(now)
      startDateTime.setDate(now.getDate() - days)
      start = startDateTime.toISOString().split('T')[0]
      end = now.toISOString().split('T')[0]
    }

    return { start, end }
  }

  const loadData = async () => {
    try {
      setLoading(true)
      setError(null)
      const params = getDateParams()
      const [ov, u, b, m, t] = await Promise.all([
        adminLlmService.overview(params),
        adminLlmService.byUser({ ...params, limit: 10 }),
        adminLlmService.byBot({ ...params, limit: 10 }),
        adminLlmService.byModel({ ...params, limit: 10 }),
        adminLlmService.trainingUsage({ ...params, limit: 20 }),
      ])
      setOverview(ov)
      setByUser(u)
      setByBot(b)
      setByModel(m)
      setTrainingUsage(t)
    } catch (e: any) {
      setError(e?.message || 'Failed to load analytics')
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }

  useEffect(() => {
    loadData()
  }, [dateRange, startDate, endDate])

  const handleRefresh = () => {
    setRefreshing(true)
    loadData()
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-3">
          <RefreshCw className="w-8 h-8 text-blue-600 animate-spin" />
          <p className="text-gray-600">Loading LLM analytics...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-6">
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
              <div>
                <h3 className="font-medium text-red-900">Failed to load analytics</h3>
                <p className="text-sm text-red-700 mt-1">{error}</p>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-3"
                  onClick={handleRefresh}
                >
                  Try Again
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">LLM Usage Analytics</h1>
          <p className="text-gray-600 mt-1">Platform-wide AI model consumption and performance</p>
        </div>

        <div className="flex items-center gap-3">
          {/* Date Range Filter */}
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-gray-500" />
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value as any)}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="today">Today</option>
              <option value="yesterday">Yesterday</option>
              <option value="7d">Last 7 Days</option>
              <option value="30d">Last 30 Days</option>
              <option value="90d">Last 90 Days</option>
              <option value="all">All Time</option>
              <option value="custom">Custom Range</option>
            </select>
          </div>

          {/* Custom Date Inputs */}
          {dateRange === 'custom' && (
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Start date"
              />
              <span className="text-gray-500">to</span>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="End date"
              />
            </div>
          )}

          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={refreshing}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Overview Metrics */}
      {overview && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <MetricCard
            title="Total Cost"
            value={`$${overview.cost_usd.toFixed(4)}`}
            subtitle="Platform-wide LLM spend"
            icon={DollarSign}
            iconColor="text-green-600"
          />
          <MetricCard
            title="Total Tokens"
            value={overview.total_tokens.toLocaleString()}
            subtitle={`In: ${overview.input_tokens.toLocaleString()} • Out: ${overview.output_tokens.toLocaleString()}`}
            icon={Zap}
            iconColor="text-yellow-600"
          />
          <MetricCard
            title="Avg Latency"
            value={formatLatency(overview.avg_latency_ms)}
            subtitle="Average response time"
            icon={Clock}
            iconColor="text-purple-600"
          />
          <MetricCard
            title="Total Requests"
            value={overview.total_requests?.toLocaleString() || '—'}
            subtitle="API calls made"
            icon={Activity}
            iconColor="text-blue-600"
          />
        </div>
      )}

      {/* Usage Tables */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Users */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-600" />
              <CardTitle>Top Users by Cost</CardTitle>
            </div>
            <CardDescription>Highest LLM spending users</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-2 text-sm font-medium text-gray-600">User Email</th>
                    <th className="text-right py-3 px-2 text-sm font-medium text-gray-600">Requests</th>
                    <th className="text-right py-3 px-2 text-sm font-medium text-gray-600">Tokens</th>
                    <th className="text-right py-3 px-2 text-sm font-medium text-gray-600">Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {byUser.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="py-8 text-center text-gray-500 text-sm">
                        No usage data available
                      </td>
                    </tr>
                  ) : (
                    byUser.map((r, i) => (
                      <tr key={i} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-3 px-2 text-sm text-gray-900">
                          {r.user_email || 'Unknown'}
                        </td>
                        <td className="py-3 px-2 text-sm text-gray-700 text-right">
                          {r.requests.toLocaleString()}
                        </td>
                        <td className="py-3 px-2 text-sm text-gray-700 text-right">
                          {r.total_tokens.toLocaleString()}
                        </td>
                        <td className="py-3 px-2 text-sm font-medium text-gray-900 text-right">
                          ${r.cost_usd.toFixed(4)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Top Bots */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Bot className="w-5 h-5 text-purple-600" />
              <CardTitle>Top Bots by Cost</CardTitle>
            </div>
            <CardDescription>Highest consuming chatbots</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-2 text-sm font-medium text-gray-600">Bot Name</th>
                    <th className="text-left py-3 px-2 text-sm font-medium text-gray-600">Owner</th>
                    <th className="text-right py-3 px-2 text-sm font-medium text-gray-600">Requests</th>
                    <th className="text-right py-3 px-2 text-sm font-medium text-gray-600">Tokens</th>
                    <th className="text-right py-3 px-2 text-sm font-medium text-gray-600">Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {byBot.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="py-8 text-center text-gray-500 text-sm">
                        No usage data available
                      </td>
                    </tr>
                  ) : (
                    byBot.map((r, i) => (
                      <tr key={i} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-3 px-2 text-sm text-gray-900 font-medium">
                          {r.bot_name || 'Unknown'}
                        </td>
                        <td className="py-3 px-2 text-sm text-gray-600">
                          {r.owner_email || 'Unknown'}
                        </td>
                        <td className="py-3 px-2 text-sm text-gray-700 text-right">
                          {r.requests.toLocaleString()}
                        </td>
                        <td className="py-3 px-2 text-sm text-gray-700 text-right">
                          {r.total_tokens.toLocaleString()}
                        </td>
                        <td className="py-3 px-2 text-sm font-medium text-gray-900 text-right">
                          ${r.cost_usd.toFixed(4)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Model Mix */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Cpu className="w-5 h-5 text-indigo-600" />
            <CardTitle>Model Distribution</CardTitle>
          </div>
          <CardDescription>AI models used across the platform</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-2 text-sm font-medium text-gray-600">Provider</th>
                  <th className="text-left py-3 px-2 text-sm font-medium text-gray-600">Model</th>
                  <th className="text-right py-3 px-2 text-sm font-medium text-gray-600">Requests</th>
                  <th className="text-right py-3 px-2 text-sm font-medium text-gray-600">Tokens</th>
                  <th className="text-right py-3 px-2 text-sm font-medium text-gray-600">Cost</th>
                </tr>
              </thead>
              <tbody>
                {byModel.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="py-8 text-center text-gray-500 text-sm">
                      No model data available
                    </td>
                  </tr>
                ) : (
                  byModel.map((r, i) => (
                    <tr key={i} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-2 text-sm">
                        <Badge variant="secondary">{r.provider}</Badge>
                      </td>
                      <td className="py-3 px-2 text-sm text-gray-900 font-medium">
                        {r.model}
                      </td>
                      <td className="py-3 px-2 text-sm text-gray-700 text-right">
                        {r.requests.toLocaleString()}
                      </td>
                      <td className="py-3 px-2 text-sm text-gray-700 text-right">
                        {r.total_tokens.toLocaleString()}
                      </td>
                      <td className="py-3 px-2 text-sm font-medium text-gray-900 text-right">
                        ${r.cost_usd.toFixed(4)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Training/Document Upload Usage */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-orange-600" />
            <CardTitle>Training Data Usage</CardTitle>
          </div>
          <CardDescription>LLM usage for document metadata extraction (DeepSeek)</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-2 text-sm font-medium text-gray-600">User Email</th>
                  <th className="text-left py-3 px-2 text-sm font-medium text-gray-600">Bot Name</th>
                  <th className="text-right py-3 px-2 text-sm font-medium text-gray-600">Extractions</th>
                  <th className="text-right py-3 px-2 text-sm font-medium text-gray-600">Input</th>
                  <th className="text-right py-3 px-2 text-sm font-medium text-gray-600">Output</th>
                  <th className="text-right py-3 px-2 text-sm font-medium text-gray-600">Total Tokens</th>
                  <th className="text-right py-3 px-2 text-sm font-medium text-gray-600">Cost</th>
                </tr>
              </thead>
              <tbody>
                {trainingUsage.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="py-8 text-center text-gray-500 text-sm">
                      No training usage data available
                    </td>
                  </tr>
                ) : (
                  trainingUsage.map((r, i) => (
                    <tr key={i} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-2 text-sm text-gray-900">
                        {r.user_email || 'Unknown'}
                      </td>
                      <td className="py-3 px-2 text-sm text-gray-700">
                        {r.bot_name || 'Unknown'}
                      </td>
                      <td className="py-3 px-2 text-sm text-gray-700 text-right">
                        {r.extractions.toLocaleString()}
                      </td>
                      <td className="py-3 px-2 text-sm text-gray-600 text-right">
                        {r.input_tokens.toLocaleString()}
                      </td>
                      <td className="py-3 px-2 text-sm text-gray-600 text-right">
                        {r.output_tokens.toLocaleString()}
                      </td>
                      <td className="py-3 px-2 text-sm text-gray-700 text-right font-medium">
                        {r.total_tokens.toLocaleString()}
                      </td>
                      <td className="py-3 px-2 text-sm font-medium text-gray-900 text-right">
                        ${r.cost_usd.toFixed(4)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {trainingUsage.length > 0 && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="flex justify-between items-center text-sm">
                <span className="text-gray-600">
                  Total Training Extractions: {trainingUsage.reduce((sum, r) => sum + r.extractions, 0).toLocaleString()}
                </span>
                <span className="text-gray-900 font-semibold">
                  Total Cost: ${trainingUsage.reduce((sum, r) => sum + r.cost_usd, 0).toFixed(4)}
                </span>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
